import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Settings, Clock, Clock3, User, ChefHat, Coffee, Check, Truck, AlertTriangle, X, Zap, CookingPot, Utensils, Cookie, Save, RotateCcw, Store } from 'lucide-react';
import { Building2, FileText, Info } from 'lucide-react';
import { fetchOrdersFromApi, patchMesaItemFromApi, patchMesaItemsFromApi, getNamespaceFromUrl, fetchSetoresFromApi } from './services/service';
import SimpleErrorScreen from './components/SimpleErrorScreen';
import Clarity from '@microsoft/clarity';

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

const projectId = "s613bb4gep";
Clarity.init(projectId);

const VERSION = '1.3.0.0'

const CNPJ = '00.000.000/0000-00'

const ICONS = {
  Clock, Coffee, Check, Clock3, Truck, AlertTriangle, X, Zap, User, ChefHat, Settings, CookingPot, Utensils, Cookie, Save, RotateCcw, Store
};

const STATUS_CONFIG = {
  waiting: { label: 'Em espera', color: 'bg-gray-400', textColor: 'text-white', icon: 'Clock' },
  preparing: { label: 'Em Preparo', color: 'bg-orange-500', textColor: 'text-white', icon: 'CookingPot' },
  priority: { label: 'Prioridade', color: 'bg-purple-500', textColor: 'text-white', icon: 'Zap' },
  ready: { label: 'Pronto', color: 'bg-green-500', textColor: 'text-white', icon: 'Check' },
  delivered: { label: 'Entregue', color: 'bg-blue-500', textColor: 'text-white', icon: 'Utensils' },
  cancelled: { label: 'Cancelado', color: 'bg-red-400', textColor: 'text-white', icon: 'X' },
  'pending-cancel': { label: 'Cancelamento', color: 'bg-yellow-500', textColor: 'text-white', icon: 'AlertTriangle' },
  delayed: { label: 'Atrasado', color: 'bg-red-700', textColor: 'text-white', icon: 'AlertTriangle' }
};

const DEFAULT_CONFIG = {
  refreshInterval: 5,
  showTimer: true,
  cardWidth: 350,
  cardHeight: 450,
  fontSize: 14,
  itemStatusList: ['waiting', 'preparing', 'ready', 'delivered', 'cancelled'],
  orderStatusList: [
    'priority',        // ⚡
    'delayed',         // 🔺
    'waiting',         // ⏳
    'preparing',       // 👨‍🍳
    'ready',           // ✅
    'delivered',       // 🍴
    'cancelled',       // ❌
    'pending-cancel',  // 🚫
  ],
  selectedStatuses: ['all'],
  availableSetores: [],  // Lista de setores disponíveis
  selectedSetores: ['all']  // Setores selecionados para exibição
};

const VISIBLE_STATUSES = [
  'waiting',
  'preparing',
  'ready',
  'delivered',
  // 'cancelled',
];

// ============================================================================
// LOCAL STORAGE FUNCTIONS
// ============================================================================

const STORAGE_KEY = 'kds_monitor_config';

const saveConfigToStorage = (config) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const configData = JSON.stringify(config);
      window.localStorage.setItem(STORAGE_KEY, configData);
      console.log('✅ Configurações salvas');
    }
  } catch (error) {
    console.warn('❌ Erro ao salvar configurações:', error);
  }
};

const loadConfigFromStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const configData = window.localStorage.getItem(STORAGE_KEY);
      if (configData) {
        const parsedConfig = JSON.parse(configData);
        console.log('✅ Configurações carregadas');
        return { ...DEFAULT_CONFIG, ...parsedConfig };
      }
    }
  } catch (error) {
    console.warn('❌ Erro ao carregar configurações:', error);
  }
  return DEFAULT_CONFIG;
};

const resetConfigInStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(STORAGE_KEY);
      console.log('✅ Configurações resetadas');
    }
  } catch (error) {
    console.warn('❌ Erro ao resetar configurações:', error);
  }
};

// ============================================================================
// ERROR MESSAGE HELPER
// ============================================================================

const getErrorMessage = (error) => {
  const errorStr = error?.toString()?.toLowerCase() || '';

  if (errorStr.includes('network') || errorStr.includes('fetch')) {
    return 'Sem conexão com a internet. Verifique sua rede.';
  }

  if (errorStr.includes('timeout')) {
    return 'Tempo limite excedido. Servidor demorou para responder.';
  }

  if (errorStr.includes('500')) {
    return 'Erro interno do servidor. Tente novamente em alguns instantes.';
  }

  if (errorStr.includes('404')) {
    return 'Serviço não encontrado. Verifique a configuração.';
  }

  return 'Não foi possível conectar com o servidor. Tente novamente.';
};

// ============================================================================
// API SERVICE
// ============================================================================

const mockFetchConfig = () => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        success: true,
        data: loadConfigFromStorage()
      });
    }, 500);
  });
};

const mockSaveConfig = (config) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      saveConfigToStorage(config);
      resolve({
        success: true,
        message: 'Configurações salvas com sucesso',
        data: config
      });
    }, 500);
  });
};

// App.js
export const apiService = {
  updateItemStatus: async (orderId, orderMesaId, orderGuidItem, itemIndex, newStatus) => {
    try {
      const namespace = getNamespaceFromUrl();
      return await patchMesaItemFromApi(namespace, newStatus, orderMesaId, orderGuidItem);
    } catch (error) {
      console.error('Erro ao atualizar status do item:', error);
      throw error;
    }
  },

  updateOrderStatus: async (orderId, orderSeq, newStatus) => {
    try {
      const namespace = getNamespaceFromUrl();
      return await patchMesaItemsFromApi(namespace, orderId, orderSeq, newStatus)
    } catch (error) {
      console.error('Erro ao atualizar status do pedido:', error);
      throw error;
    }
  },

  fetchOrders: async (selectedSetores = ['all']) => {
    try {
      const namespace = getNamespaceFromUrl();
      // Filtra apenas os setores que não são 'all'
      const setoresToSend = selectedSetores.filter(s => s !== 'all');
      const result = await fetchOrdersFromApi(namespace, setoresToSend);
      return result;
    } catch (error) {
      console.error('Erro ao buscar pedidos:', error);
      throw error;
    }
  },

  fetchSetores: async () => {
    try {
      const namespace = getNamespaceFromUrl();
      return await fetchSetoresFromApi(namespace);
    } catch (error) {
      console.error('Erro ao buscar setores:', error);
      throw error;
    }
  },

  fetchConfig: async () => {
    try {
      return await mockFetchConfig();
    } catch (error) {
      console.error('Erro ao buscar configurações:', error);
      throw error;
    }
  },

  saveConfig: async (config) => {
    try {
      return await mockSaveConfig(config);
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      throw error;
    }
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.waiting;

const renderIcon = (status, size = 'w-3 h-3') => {
  const statusConfig = getStatusConfig(status);
  const IconComponent = ICONS[statusConfig.icon];
  return IconComponent ? <IconComponent className={`${size} text-white`} /> : <Clock3 className={`${size} text-white`} />;
};

// ============================================================================
// SIMPLE COMPONENTS
// ============================================================================

const ToastMessage = ({ message, type = 'success', onClose }) => {
  let bgColor = '';
  let textColor = '';

  switch (type) {
    case 'success':
      bgColor = 'bg-green-500';
      textColor = 'text-white';
      break;
    case 'error':
      bgColor = 'bg-red-500';
      textColor = 'text-white';
      break;
    case 'info':
      bgColor = 'bg-blue-500';
      textColor = 'text-white';
      break;
    default:
      bgColor = 'bg-gray-800';
      textColor = 'text-white';
  }

  return (
    <div className={`fixed bottom-4 right-4 z-50 ${bgColor} ${textColor} px-4 py-3 rounded shadow-lg flex items-center space-x-2`}>
      <span>{message}</span>
      <button onClick={onClose} className="ml-2">
        <X size={18} />
      </button>
    </div>
  );
};

const StatusIcon = ({ status, config = DEFAULT_CONFIG }) => {
  const statusConfig = getStatusConfig(status);
  const fontSize = config?.fontSize || DEFAULT_CONFIG.fontSize;
  const iconSize = fontSize >= 16 ? 'w-5 h-5' : 'w-4 h-4';
  const containerSize = fontSize >= 16 ? 'w-7 h-7' : 'w-6 h-6';

  return (
    <div className={`${containerSize} rounded-full ${statusConfig.color} flex items-center justify-center`}>
      {renderIcon(status, iconSize)}
    </div>
  );
};

const StatusBadge = ({ status, config = DEFAULT_CONFIG }) => {
  const statusConfig = getStatusConfig(status);
  const fontSize = config?.fontSize || DEFAULT_CONFIG.fontSize;
  const textSize = fontSize >= 18 ? 'text-base' : fontSize >= 16 ? 'text-sm' : 'text-xs';
  const padding = fontSize >= 16 ? 'px-2 py-1.5' : 'px-1.5 py-1';

  return (
    <span className={`${padding} rounded-md ${textSize} font-bold ${statusConfig.color} ${statusConfig.textColor}`}>
      {statusConfig.label}
    </span>
  );
};

const MenuButton = ({ onClick, title = "Alterar status", config = DEFAULT_CONFIG }) => {
  const fontSize = config?.fontSize || DEFAULT_CONFIG.fontSize;
  const buttonSize = fontSize >= 16 ? 'w-8 h-8' : 'w-6 h-6';
  const iconSize = fontSize >= 16 ? 'w-5 h-5' : 'w-4 h-4';

  return (
    <button
      onClick={onClick}
      type="button"
      className={`${buttonSize} flex items-center justify-center rounded-full hover:bg-gray-200 transition-all duration-200`}
      title={title}
    >
      <svg className={iconSize} fill="#6B7280" viewBox="0 0 20 20">
        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"></path>
      </svg>
    </button>
  );
};

// ============================================================================
// DROPDOWN COMPONENTS
// ============================================================================

const StatusDropdown = ({ isOpen, onStatusChange, currentStatus, onClose, statusList, config = DEFAULT_CONFIG }) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleStatusChange = (status) => {
    onClose();
    onStatusChange(status);
  };

  const fontSize = config?.fontSize || DEFAULT_CONFIG.fontSize;
  const iconSize = fontSize >= 16 ? 'w-4 h-4' : 'w-3 h-3';
  const containerSize = fontSize >= 16 ? 'w-8 h-8' : 'w-6 h-6';

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      <div className="absolute right-0 top-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-2">
        <div className="flex items-center space-x-2">
          {VISIBLE_STATUSES
            .map((status) => {
              const statusConfig = STATUS_CONFIG[status];
              const isActive = currentStatus === status;
              const Icon = ICONS[statusConfig.icon] || ICONS.Clock;

              return (
                <button
                  key={status}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusChange(status);
                  }}
                  type="button"
                  className={`${containerSize} rounded-full flex items-center justify-center transition-all duration-200
                  hover:scale-110 ${isActive ? 'ring-2 ring-blue-400' : 'opacity-70 hover:opacity-100'} ${statusConfig.color}`}
                  title={statusConfig.label}
                >
                  <Icon className={`${iconSize} text-white`} />
                </button>
              );
            })}
        </div>
      </div>
    </>
  );
};

const OrderStatusDropdown = ({
  isOpen,
  onStatusChange,
  currentStatus,
  onClose,
  statusList,
  config = DEFAULT_CONFIG
}) => {
  useEffect(() => {
    const handleKeyDown = e => e.key === 'Escape' && onClose();
    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // pega só os que estão em statusList *e* em VISIBLE_STATUSES, mantendo a ordem de VISIBLE_STATUSES
  const options = [
    'priority',
    'waiting',
    'preparing',
    'ready',
    'delivered',
  ];;

  const fontSize = config.fontSize || DEFAULT_CONFIG.fontSize;
  const textSize = fontSize >= 16 ? 'text-base' : 'text-sm';
  const minWidth = fontSize >= 16 ? 'min-w-[180px]' : 'min-w-[160px]';
  const padding = fontSize >= 16 ? 'px-4 py-3' : 'px-3 py-2';

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className={`absolute right-0 bottom-8 z-50 bg-white border rounded-lg shadow-lg py-1 ${minWidth}`}>
        {options.map(status => {
          const { label, priority } = STATUS_CONFIG[status];
          const isCurrent = currentStatus === status;
          return (
            <button
              key={status}
              type="button"
              onClick={e => { e.stopPropagation(); onClose(); onStatusChange(status); }}
              className={`w-full ${padding} text-left ${textSize} flex items-center justify-between space-x-2
                ${isCurrent ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <div className="flex items-center space-x-2">
                <StatusIcon status={status} config={config} />
                <span>{label}</span>
              </div>
              <span className="text-xs font-medium">{priority}</span>
            </button>
          );
        })}
      </div>
    </>
  );
};

// ============================================================================
// ITEM COMPONENT
// ============================================================================

const ItemCard = ({ item, itemIndex, order, updateItemStatus, toggleMenu, openMenus, closeAllMenus, itemStatusList, config = DEFAULT_CONFIG }) => {
  const itemKey = `${order.id}-${itemIndex}`;
  const fontSize = config?.fontSize || DEFAULT_CONFIG.fontSize;

  const getItemStyles = () => {
    const textSize = fontSize >= 18 ? 'text-lg' : fontSize >= 16 ? 'text-base' : 'text-sm';
    const baseStyles = `break-words flex-1 transition-all duration-200 ${textSize}`;
    switch (item.status) {
      case 'cancelled': return `${baseStyles} line-through text-red-500 opacity-70`;
      case 'delivered': return `${baseStyles} text-gray-400 opacity-60`;
      case 'pending-cancel': return `${baseStyles} text-yellow-700 font-medium`;
      default: return baseStyles;
    }
  };

  const getContainerStyles = () => {
    const padding = fontSize >= 16 ? 'p-4' : 'p-3';
    const baseStyles = `flex items-center cursor-pointer ${padding} rounded-lg border transition-all duration-200 shadow-sm hover:shadow-md`;
    const statusStyles = {
      'delivered': 'bg-gray-50 opacity-70 border-gray-200',
      'cancelled': 'bg-red-50 border-red-200',
      'pending-cancel': 'bg-yellow-50 border-yellow-400 border-2 animate-pulse',
      'ready': 'bg-green-50 border-green-200',
      'preparing': 'bg-orange-50 border-orange-200',
      'delayed': 'bg-red-100 border-red-500 border-2 animate-pulse shadow-lg' // ← ADICIONAR ESTA LINHA
    };
    return `${baseStyles} ${statusStyles[item.status] || 'bg-gray-50 border-gray-200'}`;
  };

  // Função para processar markdown básico e quebras de linha
  const processTextContent = (text) => {
    if (!text) return '';

    // Processa markdown básico
    let processedText = text
      // Bold: **texto** ou __texto__
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      // Italic: *texto* ou _texto_
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      // Code: `texto`
      .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>')
      // Strike: ~~texto~~
      .replace(/~~(.*?)~~/g, '<del>$1</del>')
      // Quebras de linha: \n para <br>
      .replace(/\n/g, '<br/>');

    return processedText;
  };

  const handleClick = (e) => {
    const buttonClicked = e.target.closest('button');
    const menuClicked = e.target.closest('.dropdown-menu');

    if (buttonClicked || menuClicked) return;
    // retorna se o status for qualquer um dos três
    if (['pending-cancel', 'delivered', 'cancelled'].includes(item.status)) return;

    let nextStatus;

    // Tratamento especial para status "delayed" - vai direto para "ready"
    if (item.status === 'delayed') {
      nextStatus = 'ready';
    } else {
      // Lógica normal para outros status
      const allowedStatuses = ['waiting', 'preparing', 'ready', 'delivered'];
      const currentIndex = allowedStatuses.indexOf(item.status);
      const nextIndex = (currentIndex + 1) % allowedStatuses.length;
      nextStatus = allowedStatuses[nextIndex];
    }
    // 🔥 ADICIONAR ANTES DA LÓGICA
    Clarity.event('item_card_click', {
      orderId: order.id,
      itemIndex: itemIndex,
      currentStatus: item.status,
      nextStatus: nextStatus
    })
    updateItemStatus(order.id, order.mesa, order.items[itemIndex].guid_item, itemIndex, nextStatus);
  };

  const obsTextSize = fontSize >= 16 ? 'text-sm' : 'text-xs';
  const buttonTextSize = fontSize >= 16 ? 'text-base' : 'text-sm';
  const buttonPadding = fontSize >= 16 ? 'px-6 py-3' : 'px-4 py-2';

  return (
    <div className={getContainerStyles()} onClick={handleClick}>
      <div className="flex-1 flex items-start gap-2">
        <StatusIcon status={item.status} config={config} />
        <div className="flex-1">
          <div
            className={getItemStyles()}
            dangerouslySetInnerHTML={{ __html: processTextContent(item.text) }}
          />
          {item.obs && (
            <div className={`${obsTextSize} text-red-600 font-medium mt-1`}>
              <span>OBS: </span>
              <span
                dangerouslySetInnerHTML={{ __html: processTextContent(item.obs) }}
              />
            </div>
          )}
          {item.status === 'pending-cancel' && (
            <div className="mt-2 flex justify-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  updateItemStatus(order.id, order.mesa, order.items[itemIndex].guid_item, itemIndex, 'cancelled');
                }}
                className={`${buttonPadding} bg-red-500 text-white ${buttonTextSize} font-medium rounded-lg hover:bg-red-600`}
              >
                ✓ Confirmar Cancelamento
              </button>
            </div>
          )}
        </div>
        <div className="relative">
          <MenuButton
            onClick={(e) => {
              e.stopPropagation();
              toggleMenu(itemKey);
            }}
            config={config}
          />
          <StatusDropdown
            isOpen={openMenus.has(itemKey)}
            onStatusChange={(status) => updateItemStatus(order.id, order.mesa, order.items[itemIndex].guid_item, itemIndex, status)}
            currentStatus={item.status}
            onClose={closeAllMenus}
            statusList={itemStatusList}
            config={config}
          />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ORDER CARD COMPONENT
// ============================================================================

const OrderCard = ({ order, config = DEFAULT_CONFIG, updateItemStatus, updateOrderStatus, toggleMenu, openMenus, closeAllMenus }) => {
  const statusConfig = getStatusConfig(order.status);
  const fontSize = config?.fontSize || DEFAULT_CONFIG.fontSize;
  const cardWidth = config?.cardWidth || DEFAULT_CONFIG.cardWidth;
  const cardHeight = config?.cardHeight || DEFAULT_CONFIG.cardHeight;
  const itemStatusList = config?.itemStatusList || DEFAULT_CONFIG.itemStatusList;
  const orderStatusList = config?.orderStatusList || DEFAULT_CONFIG.orderStatusList;
  const showTimer = config?.showTimer !== undefined ? config.showTimer : DEFAULT_CONFIG.showTimer;

  const titleSize = fontSize >= 18 ? 'text-xl' : fontSize >= 16 ? 'text-lg' : 'text-base';
  const infoSize = fontSize >= 16 ? 'text-base' : 'text-sm';
  const smallSize = fontSize >= 16 ? 'text-sm' : 'text-xs';
  const padding = fontSize >= 16 ? 'p-5' : 'p-4';
  const spacing = fontSize >= 16 ? 'space-y-3' : 'space-y-2';

  return (
    <div
      className="bg-white rounded-lg shadow-md border flex flex-col transition-all duration-300 hover:shadow-xl"
      style={{
        width: `${cardWidth}px`,
        height: `${cardHeight}px`,
        fontSize: `${fontSize}px`
      }}
    >
      <div className={`h-1 ${statusConfig.color} rounded-t-lg flex-shrink-0`}></div>

      <div className="flex flex-col h-full overflow-hidden">
        <div className={`${padding} flex-shrink-0`}>
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                {order.status === 'delayed' && (() => {
                  const Icon = ICONS[STATUS_CONFIG.delayed.icon] || ICONS.Clock;
                  return (
                    <div className="w-6 h-6 bg-red-700 rounded-full flex items-center justify-center">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                  );
                })()}
                <h3 className={`${titleSize} font-bold`}>#{order.id}</h3>
              </div>
              <p className={`${infoSize} text-gray-600 truncate`}>
                Mesa {order.mesa} {order.comanda && `| Comanda ${order.comanda}`}
              </p>
              <p className={`${infoSize} text-gray-600 truncate`}>{order.cliente}</p>
            </div>
            <div className="text-right flex-shrink-0 ml-2">
              <StatusBadge status={order.status} config={config} />
              {showTimer && order.tempo && (
                <p className={`${infoSize} font-medium mt-1`}>
                  {order.status === 'delayed' && order.atraso && (
                    <span className="text-red-600 italic">Atraso {order.atraso} </span>
                  )}
                  <span className="font-bold">{order.tempo}</span>
                </p>
              )}
            </div>
          </div>
        </div>

        <div className={`flex-1 overflow-y-auto ${padding.replace('p-', 'px-')}`}>
          <div className={`${spacing} mb-3`}>
            {order.items?.map((item, itemIndex) => (
              <ItemCard
                key={`${order.id}-item-${itemIndex}`}
                item={item}
                itemIndex={itemIndex}
                order={order}
                updateItemStatus={updateItemStatus}
                toggleMenu={toggleMenu}
                openMenus={openMenus}
                closeAllMenus={closeAllMenus}
                itemStatusList={itemStatusList}
                config={config}
              />
            ))}
          </div>
        </div>

        <div className={`flex-shrink-0 ${padding}`}>
          {order.obs && (
            <div className={`${infoSize} font-medium mb-3 text-red-600`}>
              OBS.: {order.obs}
            </div>
          )}

          <div className="flex justify-between items-center pt-3 border-t">
            <span className={`${smallSize} text-gray-500`}>Origem: Mesa</span>
            <div className="relative">
              <div className="flex items-center space-x-1">
                <StatusIcon status={order.status} config={config} />
                <MenuButton
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMenu(order.id);
                  }}
                  config={config}
                />
              </div>
              <OrderStatusDropdown
                isOpen={openMenus.has(order.id)}
                onStatusChange={(status) => updateOrderStatus(order.mesa, order.seq, status)}
                currentStatus={order.status}
                onClose={closeAllMenus}
                statusList={orderStatusList}
                config={config}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// HEADER COMPONENTS
// ============================================================================

const LiveClock = ({ time, config = DEFAULT_CONFIG }) => {
  const fontSize = config?.fontSize || DEFAULT_CONFIG.fontSize;
  const textSize = fontSize >= 16 ? 'text-base' : 'text-sm';
  const iconSize = fontSize >= 16 ? 'w-5 h-5' : 'w-4 h-4';

  return (
    <div className="flex items-center gap-1 px-3 py-1 bg-blue-50 rounded-full">
      <Clock className={`${iconSize} text-blue-500`} />
      <span
        className={`${textSize} text-blue-700 font-bold font-mono text-center`}
        style={{ width: '4.5rem', display: 'inline-block' }}
      >
        {time.toLocaleTimeString()}
      </span>
    </div>
  );
};

const StatusLegend = ({ config = DEFAULT_CONFIG }) => {
  const fontSize = config?.fontSize || DEFAULT_CONFIG.fontSize;
  const textSize = fontSize >= 16 ? 'text-base' : 'text-sm';
  const labelSize = fontSize >= 16 ? 'text-sm' : 'text-xs';

  return (
    <div className="flex items-center space-x-4">
      <span className={`${textSize} font-medium text-gray-700`}>Legenda</span>
      <div className="flex gap-3">
        {VISIBLE_STATUSES.map((key) => {
          const status = STATUS_CONFIG[key];
          return (
            <div key={key} className="flex items-center gap-1 min-w-[80px]">
              <StatusIcon status={key} config={config} />
              <span className={`${labelSize} text-gray-600`}>{status.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// ACCORDION COMPONENT
// ============================================================================

const AccordionItem = ({ title, children, isOpen, onToggle, icon: Icon, bgColor = "bg-gray-50" }) => {
  return (
    <div className={`${bgColor} rounded-lg overflow-hidden transition-all duration-200`}>
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-opacity-80 transition-colors"
      >
        <div className="flex items-center space-x-2">
          {Icon && <Icon className="w-5 h-5 text-gray-600" />}
          <h3 className="text-lg font-semibold text-gray-700">{title}</h3>
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div className={`transition-all duration-200 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
        <div className="px-4 pb-4">
          {children}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// CONFIGURATION SIDEBAR
// ============================================================================

const ConfigSidebar = ({ isOpen, onClose, config, updateConfig, setoresData = [] }) => {
  const [localConfig, setLocalConfig] = useState(config);
  const [openAccordions, setOpenAccordions] = useState({
    filters: false,
    setores: false,  // Novo accordion para setores
    layout: false,
    system: false
  });

  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  const handleConfigChange = (key, value) => {
    const newConfig = { ...localConfig, [key]: value };
    setLocalConfig(newConfig);
    updateConfig(newConfig);
  };

  const handleStatusFilterChange = (status) => {
    Clarity.event('filter_change', { selectedStatus: status, previousFilter: localConfig.selectedStatuses });

    let prev = localConfig.selectedStatuses.filter(s => s !== 'all');
    if (status === 'all') {
      handleConfigChange('selectedStatuses', ['all']);
    } else {
      if (prev.includes(status)) {
        prev = prev.filter(s => s !== status);
        handleConfigChange('selectedStatuses', prev.length ? prev : ['all']);
      } else {
        handleConfigChange('selectedStatuses', [...prev, status]);
      }
    }
  };

  const handleSetorFilterChange = (setor) => {
    Clarity.event('setor_filter_change', { selectedSetor: setor, previousFilter: localConfig.selectedSetores });

    let prev = localConfig.selectedSetores.filter(s => s !== 'all');
    if (setor === 'all') {
      handleConfigChange('selectedSetores', ['all']);
    } else {
      if (prev.includes(setor)) {
        prev = prev.filter(s => s !== setor);
        handleConfigChange('selectedSetores', prev.length ? prev : ['all']);
      } else {
        handleConfigChange('selectedSetores', [...prev, setor]);
      }
    }
  };

  const getFilterDisplayText = () => {
    if (localConfig.selectedStatuses.includes('all') || localConfig.selectedStatuses.length === 0) return 'Todos os Status';
    if (localConfig.selectedStatuses.length === 1) return getStatusConfig(localConfig.selectedStatuses[0]).label;
    return `${localConfig.selectedStatuses.length} Status Selecionados`;
  };

  const getSetorDisplayText = () => {
    if (localConfig.selectedSetores.includes('all') || localConfig.selectedSetores.length === 0) return 'Todos os Setores';
    if (localConfig.selectedSetores.length === 1) {
      const setorNome = localConfig.selectedSetores[0];
      const setorData = Array.isArray(setoresData) ? setoresData.find(s => {
        if (typeof s === 'string') return s === setorNome;
        if (s && typeof s === 'object' && s.nome) return s.nome === setorNome;
        return false;
      }) : null;

      if (setorData && typeof setorData === 'object' && setorData.produtos_count !== undefined) {
        return `${setorNome} (${setorData.produtos_count} produtos)`;
      }
      return setorNome;
    }
    return `${localConfig.selectedSetores.length} Setores Selecionados`;
  };

  const toggleAccordion = (key) => {
    setOpenAccordions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = () => {
    Clarity.event('config_save', {
      cardWidth: localConfig.cardWidth,
      cardHeight: localConfig.cardHeight,
      fontSize: localConfig.fontSize,
      refreshInterval: localConfig.refreshInterval,
      selectedSetores: localConfig.selectedSetores
    });

    updateConfig(localConfig);
    saveConfigToStorage(localConfig);
    onClose();
  };

  const handleReset = () => {
    if (window.confirm('Tem certeza que deseja restaurar as configurações padrão?')) {
      Clarity.event('config_reset');
      resetConfigInStorage();
      const resetConfig = {
        ...DEFAULT_CONFIG,
        availableSetores: localConfig.availableSetores // Mantém os setores disponíveis
      };
      setLocalConfig(resetConfig);
      updateConfig(resetConfig);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-xl z-50 transform transition-transform">
        <div className="flex flex-col h-full">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800 flex items-center">
                <Settings className="w-6 h-6 mr-2 text-gray-600" />
                Configurações
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">

              {/* FILTROS E EXIBIÇÃO */}
              <AccordionItem
                title="Filtros de Status"
                icon={Clock}
                bgColor="bg-blue-50"
                isOpen={openAccordions.filters}
                onToggle={() => toggleAccordion('filters')}
              >
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">
                      {getFilterDisplayText()}
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleConfigChange('selectedStatuses', localConfig.orderStatusList)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Todos
                      </button>
                      <button
                        onClick={() => handleConfigChange('selectedStatuses', ['all'])}
                        className="text-xs text-red-600 hover:text-red-800 font-medium"
                      >
                        Limpar
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {localConfig.orderStatusList.map((key) => {
                      const status = STATUS_CONFIG[key];
                      return (
                        <label key={key} className="flex items-center space-x-2 cursor-pointer hover:bg-blue-100 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={localConfig.selectedStatuses.includes(key) && !localConfig.selectedStatuses.includes('all')}
                            onChange={() => handleStatusFilterChange(key)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                          />
                          <StatusIcon status={key} config={localConfig} />
                          <span className="text-sm text-gray-700">{status.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </AccordionItem>

              {/* FILTROS DE SETORES */}
              {localConfig.availableSetores && localConfig.availableSetores.length > 0 && (
                <AccordionItem
                  title="Filtros de Setores"
                  icon={Store}
                  bgColor="bg-indigo-50"
                  isOpen={openAccordions.setores}
                  onToggle={() => toggleAccordion('setores')}
                >
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">
                        {getSetorDisplayText()}
                      </span>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleConfigChange('selectedSetores', localConfig.availableSetores)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Todos
                        </button>
                        <button
                          onClick={() => handleConfigChange('selectedSetores', ['all'])}
                          className="text-xs text-red-600 hover:text-red-800 font-medium"
                        >
                          Limpar
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                      {localConfig.availableSetores.map((setorNome, index) => {
                        if (!setorNome) return null; // Skip if setorNome is null/undefined

                        // Encontra os dados completos do setor se disponível
                        const setorData = Array.isArray(setoresData) ? setoresData.find(s => {
                          if (typeof s === 'string') return s === setorNome;
                          if (s && typeof s === 'object' && s.nome) return s.nome === setorNome;
                          return false;
                        }) : null;

                        const produtosCount = setorData && typeof setorData === 'object' ? setorData.produtos_count : undefined;

                        return (
                          <label key={setorNome || `setor-${index}`} className="flex items-center justify-between cursor-pointer hover:bg-indigo-100 p-2 rounded">
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={localConfig.selectedSetores.includes(setorNome) && !localConfig.selectedSetores.includes('all')}
                                onChange={() => handleSetorFilterChange(setorNome)}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded"
                              />
                              <Store className="w-4 h-4 text-indigo-600" />
                              <span className="text-sm text-gray-700">{setorNome}</span>
                            </div>
                            {produtosCount !== undefined && produtosCount !== null && (
                              <span className="text-xs text-gray-500">
                                {produtosCount} {produtosCount === 1 ? 'produto' : 'produtos'}
                              </span>
                            )}
                          </label>
                        );
                      }).filter(Boolean)}
                    </div>
                  </div>
                </AccordionItem>
              )}

              {/* LAYOUT E DIMENSÕES */}
              <AccordionItem
                title="Layout e Dimensões"
                icon={Building2}
                bgColor="bg-green-50"
                isOpen={openAccordions.layout}
                onToggle={() => toggleAccordion('layout')}
              >
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Largura do Card: <span className="text-green-600 font-semibold">{localConfig.cardWidth}px</span>
                    </label>
                    <input
                      type="range"
                      min="280"
                      max="800"
                      step="20"
                      value={localConfig.cardWidth}
                      onChange={(e) => handleConfigChange('cardWidth', parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>280px</span>
                      <span>800px</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Altura do Card: <span className="text-green-600 font-semibold">{localConfig.cardHeight}px</span>
                    </label>
                    <input
                      type="range"
                      min="300"
                      max="900"
                      step="20"
                      value={localConfig.cardHeight}
                      onChange={(e) => handleConfigChange('cardHeight', parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>300px</span>
                      <span>900px</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Tamanho da Fonte: <span className="text-green-600 font-semibold">{localConfig.fontSize}px</span>
                    </label>
                    <input
                      type="range"
                      min="12"
                      max="24"
                      step="1"
                      value={localConfig.fontSize}
                      onChange={(e) => handleConfigChange('fontSize', parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>12px</span>
                      <span>24px</span>
                    </div>
                  </div>
                </div>
              </AccordionItem>

              {/* SISTEMA E PERFORMANCE */}
              <AccordionItem
                title="Sistema e Performance"
                icon={Zap}
                bgColor="bg-purple-50"
                isOpen={openAccordions.system}
                onToggle={() => toggleAccordion('system')}
              >
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Intervalo de Atualização: <span className="text-purple-600 font-semibold">{localConfig.refreshInterval}s</span>
                    </label>
                    <input
                      type="range"
                      min="5"
                      max="300"
                      step="5"
                      value={localConfig.refreshInterval}
                      onChange={(e) => handleConfigChange('refreshInterval', parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>5s</span>
                      <span>5min</span>
                    </div>
                  </div>

                  <div className="bg-purple-100 p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <Info className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-800">Dica de Performance</span>
                    </div>
                    <p className="text-xs text-purple-700">
                      Intervalos menores (5-15s) mantêm dados mais atualizados, mas consomem mais recursos.
                      Para uso intenso, considere 30-60s.
                    </p>
                  </div>
                </div>
              </AccordionItem>

            </div>
          </div>

          <div className="border-t border-gray-200 p-6 bg-gray-50">
            <div className="flex space-x-3">
              <button
                onClick={handleReset}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Restaurar Padrão</span>
              </button>
              <button
                onClick={handleSave}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Save className="w-4 h-4" />
                <span>Salvar</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// MAIN COMPONENTS
// ============================================================================

const OnlineIndicator = ({ isUpdating = false, isOffline = false, config = DEFAULT_CONFIG }) => {
  const fontSize = config?.fontSize || DEFAULT_CONFIG.fontSize;
  const textSize = fontSize >= 16 ? 'text-sm' : 'text-xs';
  const dotSize = fontSize >= 16 ? 'w-2 h-2' : 'w-1.5 h-1.5';

  const getIndicatorStyles = () => {
    if (isOffline) {
      return {
        bgColor: 'bg-red-50', // Altere para vermelho claro
        dotColor: 'bg-red-500', // Altere para vermelho
        textColor: 'text-red-700', // Altere para vermelho escuro
        text: 'Offline'
      };
    } else if (isUpdating) {
      return {
        bgColor: 'bg-blue-50',
        dotColor: 'border-2 border-blue-500 border-t-transparent animate-spin',
        textColor: 'text-blue-700',
        text: 'Atualizando...'
      };
    } else {
      return {
        bgColor: 'bg-green-50',
        dotColor: 'bg-green-500 animate-pulse',
        textColor: 'text-green-700',
        text: 'Online'
      };
    }
  };

  const { bgColor, dotColor, textColor, text } = getIndicatorStyles();

  return (
    <div className={`flex items-center space-x-2 px-3 py-1 rounded-full transition-all duration-300 ${bgColor}`}>
      <div className={`${dotSize} rounded-full ${dotColor}`}></div>
      <span className={`${textSize} font-medium ${textColor}`}>
        {text}
      </span>
    </div>
  );
};




const Header = ({ time, config = DEFAULT_CONFIG, totalOrders, onOpenConfig, isUpdating = false, isOffline = false }) => {
  return (
    <div className="bg-white shadow-sm border-b px-6 py-4 flex-shrink-0">
      <div className="flex items-stretch w-full space-x-6">
        <div className="flex items-center space-x-4 min-w-[240px]">
          <img
            src="https://download.softhubs.com.br/files/LOGO/kds-softcom.png"
            alt="Softcom Tecnologia"
            className="h-10 w-auto object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        </div>
        <div className="w-px bg-gray-300" />
        <div className="flex items-center">
          <StatusLegend config={config} />
        </div>
        <div className="w-px bg-gray-300" />
        <div className="flex items-center space-x-4">
          <LiveClock
            time={time}
            config={config}
            className="min-w-[90px] font-mono text-base text-gray-800 text-center"
          />
          <OnlineIndicator isUpdating={isUpdating} isOffline={isOffline} config={config} />
        </div>
        <div className="w-px bg-gray-300" />
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              Clarity.event('config_sidebar_open');
              onOpenConfig();
            }}
            className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            title="Configurações"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};


const Footer = ({ config = DEFAULT_CONFIG }) => {
  const fontSize = config?.fontSize || DEFAULT_CONFIG.fontSize;
  const textSize = fontSize >= 16 ? 'text-sm' : 'text-xs';

  return (
    <div className="bg-white border-t px-6 py-3 flex-shrink-0">
      <div className={`${textSize} text-gray-500 flex justify-between`}>
        <div className="flex space-x-6 items-center">
          {/* <span className="flex items-center gap-1"><Building2 size={14} /> Empresa: MATRIZ</span> */}
          {/* <span className="flex items-center gap-1"><FileText size={14} /> CNPJ: {CNPJ}</span> */}
        </div>
        <div className="flex space-x-6 items-center">
          <span className="flex items-center gap-1"><Info size={14} /> v{VERSION}</span>
        </div>
      </div>
    </div>
  );
};

const MonitorView = ({ orders, config, updateItemStatus, updateOrderStatus, toggleMenu, openMenus, closeAllMenus, time, onOpenConfig, isLoading, toasts, setToasts, isOffline }) => {
  const statusFilter = config.selectedStatuses;

  const filteredOrders = statusFilter.includes('all') || statusFilter.length === 0
    ? orders
    : orders.filter(order => statusFilter.includes(order.status));

  return (
    <div className="flex flex-col h-screen">
      <Header
        time={time}
        config={config}
        totalOrders={orders.length}
        onOpenConfig={onOpenConfig}
        isUpdating={isLoading}
        isOffline={isOffline}
      />
      <div className="flex-1 overflow-y-auto bg-gray-100 p-6">
        {isLoading && filteredOrders.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600">Carregando pedidos...</p>
            </div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Nenhum pedido encontrado</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-4 justify-start">
            {filteredOrders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                config={config}
                updateItemStatus={updateItemStatus}
                updateOrderStatus={updateOrderStatus}
                toggleMenu={toggleMenu}
                openMenus={openMenus}
                closeAllMenus={closeAllMenus}
                isOffline={isOffline}
              />
            ))}
          </div>
        )}
      </div>
      <Footer config={config} />
      {toasts.map(toast => (
        <ToastMessage
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
        />
      ))}
    </div>
  );
};


// ============================================================================
// MAIN APP COMPONENT
// ============================================================================

const KDSMonitorComplete = () => {
  // Estados principais
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [orders, setOrders] = useState([]);
  const [time, setTime] = useState(new Date());
  const [openMenus, setOpenMenus] = useState(new Map());
  const [configSidebarOpen, setConfigSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [setoresData, setSetoresData] = useState([]); // Armazena dados completos dos setores

  // Estados para tratamento de erro
  const [hasError, setHasError] = useState(false);
  const [errorType, setErrorType] = useState('communication'); // 'communication' ou 'namespace'
  const [errorMessage, setErrorMessage] = useState('');
  const [isRetryingConnection, setIsRetryingConnection] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isOffline, setIsOffline] = useState(false);
  const appRef = useRef(null);
  const refreshIntervalRef = useRef(null);

  const showToast = (message, type = 'success') => {
    const toastId = Date.now();
    setToasts(prev => [...prev, { id: toastId, message, type }]);

    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== toastId));
    }, 5000);
  };

  // ✅ FUNÇÃO PARA BUSCAR SETORES
  const fetchSetores = useCallback(async () => {
    try {
      console.log('🔄 Buscando setores...');
      const setoresResponse = await apiService.fetchSetores();

      if (setoresResponse?.success && Array.isArray(setoresResponse.data)) {
        console.log('✅ Setores response completo:', setoresResponse);
        console.log('✅ Setores data:', setoresResponse.data);
        console.log('✅ Primeiro setor:', setoresResponse.data[0]);
        console.log('✅ Tipo do primeiro setor:', typeof setoresResponse.data[0]);

        if (setoresResponse.data[0] && typeof setoresResponse.data[0] === 'object') {
          console.log('✅ Chaves do primeiro setor:', Object.keys(setoresResponse.data[0]));
        }

        // Armazena dados completos
        setSetoresData(setoresResponse.data);

        // Retorna apenas os nomes dos setores para a configuração
        const nomes = setoresResponse.data
          .map(setor => {
            if (typeof setor === 'string') return setor;
            if (setor && typeof setor === 'object' && setor.nome) return setor.nome;
            return null;
          })
          .filter(nome => nome !== null && nome !== undefined);

        console.log('Nomes extraídos:', nomes);
        return nomes;
      }

      console.log('⚠️ Nenhum setor encontrado');
      return [];
    } catch (error) {
      console.error('❌ Erro ao buscar setores:', error);
      return [];
    }
  }, []);

  // ✅ FUNÇÃO ÚNICA PARA BUSCAR PEDIDOS COM TRATAMENTO DE ERRO
  const fetchOrders = useCallback(async () => {
    try {
      console.log('🔄 Buscando pedidos...');
      Clarity.event('orders_fetch_start');
      const selectedSetores = config.selectedSetores || ['all'];
      const ordersResponse = await apiService.fetchOrders(selectedSetores);
      console.log(ordersResponse);

      if (ordersResponse?.success) {
        if (ordersResponse.data?.detail === 'Namespace não encontrado') {
          Clarity.event('namespace_not_found_error');
          console.error('❌ Namespace não encontrado');
          setHasError(true);
          setErrorType('namespace');
          setErrorMessage('Namespace não encontrado. A empresa não está cadastrada no sistema.');
          return;
        }
        if (ordersResponse.data?.detail === 'Erro ao buscar mesas') {
          console.error('❌ Erro ao buscar mesas');
          setIsOffline(true);
          showToast('Erro na comunicação com o servidor. Modo offline ativado.', 'error');
          return;
        }
        if (Array.isArray(ordersResponse.data)) {
          Clarity.event('orders_fetch_success', {
            ordersCount: ordersResponse.data.length,
            selectedSetores: selectedSetores
          });
          setOrders(ordersResponse.data);
          setIsOffline(false);
          setHasError(false);
          setErrorType('communication');
          setErrorMessage('');
          console.log('✅ Pedidos carregados:', ordersResponse.data.length);
          if (!window.clarityTagsSet) {
            const namespace = getNamespaceFromUrl();
            Clarity.identify(namespace, { company: namespace, environment: 'production', version: VERSION });
            Clarity.setTag('user_type', 'kds_operator');
            Clarity.setTag('namespace', namespace);
            Clarity.setTag('app_version', VERSION);
            window.clarityTagsSet = true;
            console.log('✅ Clarity tags configuradas');
          }
        } else {
          throw new Error('Formato de resposta inválido do servidor');
        }
      } else {
        throw new Error(ordersResponse?.message || 'Resposta inválida do servidor');
      }
    } catch (err) {
      Clarity.event('orders_fetch_error', { error: err.message, errorType: getErrorMessage(err) });
      console.error('❌ Erro ao buscar pedidos:', err);
      setIsOffline(true);
      setErrorType('communication');
      setErrorMessage(getErrorMessage(err));
      showToast('Falha ao atualizar pedidos. Modo offline ativado.', 'error');
    }
  }, [config.selectedSetores]);



  // ✅ HANDLER PARA RETRY DA TELA DE ERRO
  const handleRetryConnection = async () => {
    Clarity.event('connection_retry_attempt');

    setIsRetryingConnection(true);

    try {
      await fetchOrders();
      Clarity.event('connection_retry_success');
    } catch (error) {
      Clarity.event('connection_retry_failed', {
        error: error.message
      });
      // O erro já é tratado dentro do fetchOrders
      console.log('Erro no retry:', error);
    } finally {
      setIsRetryingConnection(false);
    }
  };

  // ✅ HANDLER PARA VOLTAR AO INÍCIO (erro de namespace)
  const handleGoHome = () => {
    setHasError(false);
    setErrorType('communication');
    setErrorMessage('');
    setOrders([]);
    // Redireciona para a página inicial de seleção de namespace
    window.location.href = '/';
    console.log('Redirecionando para página inicial devido a erro de namespace');
  };

  // ✅ TIMER ÚNICO E SIMPLIFICADO
  const startRefreshTimer = useCallback(() => {
    // Limpar timer anterior
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    const interval = config.refreshInterval * 1000;
    refreshIntervalRef.current = setInterval(() => {
      console.log('⏰ Timer automático executado');
      fetchOrders();
    }, interval);

    console.log(`✅ Timer configurado para ${config.refreshInterval}s`);
  }, [config.refreshInterval, fetchOrders]);

  // ✅ CARREGAMENTO INICIAL (apenas uma vez)
  useEffect(() => {
    const initializeApp = async () => {
      Clarity.event('app_session_start', { timestamp: new Date().toISOString(), userAgent: navigator.userAgent });

      setIsLoading(true);
      try {
        // Carregar configurações
        const savedConfig = loadConfigFromStorage();

        // Buscar setores disponíveis
        const availableSetores = await fetchSetores();

        // Atualizar config com setores
        const configWithSetores = {
          ...savedConfig,
          availableSetores: availableSetores,
          // Valida e ajusta os setores salvos
          selectedSetores: (() => {
            const saved = savedConfig.selectedSetores;
            if (!saved || !Array.isArray(saved)) return ['all'];

            // Filtra apenas setores que ainda existem ou 'all'
            const validSetores = saved.filter(s =>
              s === 'all' || availableSetores.includes(s)
            );

            return validSetores.length > 0 ? validSetores : ['all'];
          })()
        };

        setConfig(configWithSetores);

        Clarity.event('config_loaded', {
          cardWidth: savedConfig.cardWidth,
          cardHeight: savedConfig.cardHeight,
          fontSize: savedConfig.fontSize,
          refreshInterval: savedConfig.refreshInterval,
          availableSetores: availableSetores.length
        });

        // Buscar pedidos iniciais será feito após o config ser atualizado
      } catch (err) {
        Clarity.event('app_initialization_error', {
          error: err.message
        });
        console.error('❌ Erro na inicialização:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []); // Sem dependências - executa apenas uma vez

  // ✅ BUSCAR PEDIDOS QUANDO CONFIG MUDA (especialmente selectedSetores)
  useEffect(() => {
    // Só busca se não estiver em erro e não for o carregamento inicial
    if (!hasError && config !== DEFAULT_CONFIG) {
      fetchOrders();
    }
  }, [config.selectedSetores, fetchOrders, hasError]);

  // ✅ GERENCIAR TIMER (recriar quando intervalo muda)
  useEffect(() => {
    if (!hasError) {
      startRefreshTimer();
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [startRefreshTimer, hasError]);

  // ✅ TIMER DO RELÓGIO
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // ✅ HANDLERS DE EVENTOS
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (appRef.current && !appRef.current.contains(event.target)) {
        closeAllMenus();
      }
    };

    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        closeAllMenus();
        setConfigSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  useEffect(() => {
    const handleOnlineStatus = () => {
      const onlineStatus = navigator.onLine;
      setIsOnline(onlineStatus);
      setIsOffline(!onlineStatus);
      if (!onlineStatus) {
        showToast('Sem conexão com a internet. Modo offline ativado.', 'error');
      } else {
        showToast('Conexão restaurada. Sincronizando dados...', 'success');
        fetchOrders();
      }
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    // Chama a função uma vez para definir o estado inicial
    handleOnlineStatus();

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, [fetchOrders]);

  const closeAllMenus = () => {
    setOpenMenus(new Map());
  };

  const toggleMenu = (menuKey) => {
    Clarity.event('status_menu_toggle', { menuKey: menuKey, isOpening: !openMenus.has(menuKey) });

    setOpenMenus(prev => {
      const newMap = new Map();
      if (!prev.has(menuKey)) {
        newMap.set(menuKey, true);
      }
      return newMap;
    });
  };

  // ✅ ATUALIZAR STATUS DO ITEM (com sincronização)
  const updateItemStatus = async (orderId, orderMesaId, orderGuidItem, itemIndex, newStatus) => {
    try {
      Clarity.event('item_status_change', {
        orderId: orderId,
        newStatus: newStatus,
        itemIndex: itemIndex
      });

      setIsLoading(true);
      const response = await apiService.updateItemStatus(orderId, orderMesaId, orderGuidItem, itemIndex, newStatus);

      if (response.success) {
        // Verificar se há erros específicos na resposta
        if (response.data?.detail === 'Namespace não encontrado') {
          setHasError(true);
          setErrorType('namespace');
          setErrorMessage('Namespace não encontrado. A empresa não está cadastrada no sistema.');
          return;
        }

        if (response.data?.detail === 'Erro ao buscar mesas') {
          setHasError(true);
          setErrorType('communication');
          setErrorMessage('Erro na comunicação com o servidor.');
          showToast('Falha na comunicação', 'error');
          return;
        }

        // 1. Atualização local imediata (feedback rápido)
        setOrders(prevOrders => prevOrders.map(order => {
          if (order.id !== orderId) return order;

          const updatedItems = order.items.map((item, index) =>
            index === itemIndex ? { ...item, status: newStatus } : item
          );

          return { ...order, items: updatedItems };
        }));

        showToast(`Status do item atualizado para ${newStatus}`, 'success');
        console.log('✅ Status do item atualizado localmente');

        // 2. Buscar dados atualizados da API (sincronização)
        try {
          console.log('🔄 Sincronizando dados após alteração de item...');
          await fetchOrders();
          console.log('✅ Dados sincronizados com sucesso');
        } catch (refreshError) {
          console.warn('⚠️ Erro na sincronização:', refreshError);
        }

        // 3. Reiniciar timer para evitar busca duplicada
        console.log('🔄 Reiniciando timer após alteração...');
        startRefreshTimer();

      } else {
        showToast(response.message || 'Falha ao atualizar status do item', 'error');
      }
    } catch (err) {
      Clarity.event('item_status_change_error', { orderId: orderId, error: err.message, newStatus: newStatus });
      console.error('❌ Erro ao atualizar status do item:', err);

      // Se for erro de comunicação, mostra a tela de erro
      if (err.message?.includes('fetch') || err.message?.includes('network')) {
        setHasError(true);
        setErrorType('communication');
        setErrorMessage(getErrorMessage(err));
      } else {
        showToast('Erro ao atualizar status do item', 'error');
      }
    } finally {
      setIsLoading(false);
      closeAllMenus();
    }
  };

  // ✅ ATUALIZAR STATUS DO PEDIDO (com sincronização)
  const updateOrderStatus = async (orderId, orderSeq, newStatus) => {
    try {
      Clarity.event('order_status_change', { orderId: orderId, orderSeq: orderSeq, newStatus: newStatus });
      setIsLoading(true);
      const response = await apiService.updateOrderStatus(orderId, orderSeq, newStatus);

      if (response.success) {
        // Verificar se há erros específicos na resposta
        if (response.data?.detail === 'Namespace não encontrado') {
          setHasError(true);
          setErrorType('namespace');
          setErrorMessage('Namespace não encontrado. A empresa não está cadastrada no sistema.');
          return;
        }

        if (response.data?.detail === 'Erro ao buscar mesas') {
          setHasError(true);
          setErrorType('communication');
          setErrorMessage('Erro na comunicação com o servidor.');
          showToast('Falha na comunicação', 'error');
          return;
        }

        // 1. Atualização local imediata (feedback rápido)
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.id === orderId ? { ...order, status: newStatus } : order
          )
        );

        showToast(`Status do pedido atualizado para ${newStatus}`, 'success');
        console.log('✅ Status do pedido atualizado localmente');

        // 2. Buscar dados atualizados da API (sincronização)
        try {
          console.log('🔄 Sincronizando dados após alteração de pedido...');
          await fetchOrders();
          console.log('✅ Dados sincronizados com sucesso');
        } catch (refreshError) {
          console.warn('⚠️ Erro na sincronização:', refreshError);
        }

        // 3. Reiniciar timer para evitar busca duplicada
        console.log('🔄 Reiniciando timer após alteração...');
        startRefreshTimer();

      } else {
        showToast(response.message || 'Falha ao atualizar status do pedido', 'error');
      }
    } catch (err) {
      Clarity.event('order_status_change_error', { orderId: orderId, error: err.message });
      console.error('❌ Erro ao atualizar status do pedido:', err);

      // Se for erro de comunicação, mostra a tela de erro
      if (err.message?.includes('fetch') || err.message?.includes('network')) {
        setHasError(true);
        setErrorType('communication');
        setErrorMessage(getErrorMessage(err));
      } else {
        showToast('Erro ao atualizar status do pedido', 'error');
      }
    } finally {
      setIsLoading(false);
      closeAllMenus();
    }
  };

  const updateConfig = async (newConfig) => {
    try {
      setIsLoading(true);
      const response = await apiService.saveConfig(newConfig);

      if (response.success) {
        setConfig(newConfig);
        saveConfigToStorage(newConfig);
        showToast('Configurações salvas com sucesso', 'success');
      } else {
        showToast(response.message || 'Falha ao salvar configurações', 'error');
      }
    } catch (err) {
      console.error('❌ Erro ao salvar configurações:', err);
      showToast('Erro ao salvar configurações', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // RENDERIZAÇÃO CONDICIONAL - TELA DE ERRO UNIFICADA
  // ============================================================================

  // Se há qualquer tipo de erro, mostra SimpleErrorScreen com configuração apropriada
  if (hasError) {
    return (
      <SimpleErrorScreen
        message={errorMessage}
        onRetry={errorType === 'communication' ? handleRetryConnection : undefined}
        onGoHome={errorType === 'namespace' ? handleGoHome : undefined}
        isRetrying={isRetryingConnection}
        errorType={errorType}
      />
    );
  }

  // ============================================================================
  // RENDERIZAÇÃO NORMAL
  // ============================================================================

  return (
    <div className="h-screen bg-gray-100 flex flex-col" ref={appRef}>
      <style jsx>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        .animate-pulse { animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
      `}</style>
      <MonitorView
        orders={orders}
        config={config}
        updateItemStatus={updateItemStatus}
        updateOrderStatus={updateOrderStatus}
        toggleMenu={toggleMenu}
        openMenus={openMenus}
        closeAllMenus={closeAllMenus}
        time={time}
        onOpenConfig={() => setConfigSidebarOpen(true)}
        isLoading={isLoading}
        toasts={toasts}
        setToasts={setToasts}
        isOffline={isOffline}
      />
      <ConfigSidebar
        isOpen={configSidebarOpen}
        onClose={() => setConfigSidebarOpen(false)}
        config={config}
        updateConfig={updateConfig}
        setoresData={setoresData}
      />
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg flex items-center space-x-2">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <span>Atualizando...</span>
          </div>
        </div>
      )}
    </div>
  );

};

export default KDSMonitorComplete;